<template>
  <component
    :is="tag"
    :type="type"
    v-touch:tap
    class="p-button-main"
    :class="[
      `p-button-main--${kind}`,
      `-${iconColor}`,
      { '-disabled': disabled },
      { '-gray-out': grayOut }
    ]"
    :disabled="disabled"
    :style="{ width: autoWidth ? '100%' : '' }"
    @click="emitClickEvent"
  >
    <component :is="childTag" class="p-button-main__icon">
      <slot name="icon" />
    </component>
    <slot />
  </component>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import IconBase from "@/common/components/atoms/IconBase.vue";
import commons from "@/common/store/modules/commons";

@Options({
  props: {
    tag: { type: String, required: false, default: "button" },
    type: { type: String, required: false, default: "button" },
    kind: { type: String, required: false, default: "main" },
    disabled: { type: Boolean, required: false, default: false },
    grayOut: { type: Boolean, required: false, default: false },
    noborder: { type: Boolean, required: false, default: false },
    autoWidth: { type: Boolean, required: false, default: false }
  },
  components: {
    IconBase
  }
})
export default class ButtonMain extends Vue {
  // Props
  tag!: string;
  type!: string;
  kind!: string;
  disabled!: boolean;
  grayOut!: boolean;
  noborder!: boolean;
  autoWidth!: boolean;

  // Computed
  get storeIconColor(): string {
    return commons.color;
  }

  get isGroupBudget(): boolean {
    return commons?.userInfo?.groupBudgetMode ?? false;
  }

  get iconColor(): string {
    return this.isGroupBudget ? "bisque" : this.storeIconColor;
  }

  get childTag(): string {
    return this.tag === "button" ? "span" : "div";
  }

  // Methods
  emitClickEvent(): void {
    this.$emit("click-button");
    this.$emit("click");
  }
}
</script>

<style lang="scss" scoped>
$pressed-colors: (
  "primary": $common-primary-pressed,
  "blue": $customize-blue-pressed,
  "green": $customize-green-pressed,
  "yellow": $customize-yellow-pressed,
  "pink": $customize-pink-pressed,
  "red": $customize-red-pressed,
  "orange": $customize-orange-pressed,
  "bisque": $customize-bisque-pressed
);

.p-button-main {
  font-size: rem(14);
  font-weight: bold;
  line-height: (21/14);
  text-align: center;
  width: rem(288);
  max-width: 100%;
  padding: rem(9) rem(16) rem(10) rem(16);
  border-radius: px(4);
  display: block;
  position: relative;
  cursor: pointer;
  word-break: keep-all;

  &.-disabled {
    pointer-events: none;
  }
  &.-background_none {
    background: none !important;
  }

  &--main {
    color: $common-white;

    &.-primary {
      background: $common-primary;
    }

    &.-blue {
      background: $customize-blue;
    }

    &.-green {
      background: $customize-green;
    }

    &.-yellow {
      background: $customize-yellow;
    }

    &.-pink {
      background: $customize-pink;
    }

    &.-red {
      background: $customize-red;
    }

    &.-orange {
      background: $customize-orange;
    }

    &.-bisque {
      background: $customize-bisque;
    }

    &.-disabled {
      background: $common-gray300;

      &:focus {
        background-color: $common-gray300;
      }
    }

    &.-gray-out {
      color: $common-black;
      border: px(1) solid $common-gray500;

      &:focus {
        opacity: $hover-opacity;
      }
    }

    &:focus,
    &.tap {
      @each $key, $color in $pressed-colors {
        &.-#{$key} {
          background-color: $color;
        }
      }
    }
  }

  &--secondary {
    background: $common-white;

    &.-primary {
      color: $common-primary;
      border: px(1) solid $common-primary;
    }

    &.-blue {
      color: $customize-blue;
      border: px(1) solid $customize-blue;
    }

    &.-green {
      color: $customize-green;
      border: px(1) solid $customize-green;
    }

    &.-yellow {
      color: $customize-yellow;
      border: px(1) solid $customize-yellow;
    }

    &.-pink {
      color: $customize-pink;
      border: px(1) solid $customize-pink;
    }

    &.-red {
      color: $customize-red;
      border: px(1) solid $customize-red;
    }

    &.-orange {
      color: $customize-orange;
      border: px(1) solid $customize-orange;
    }
    
    &.-bisque {
      color: $customize-bisque;
      border: px(1) solid $customize-bisque;
    }

    &.-disabled {
      color: $common-gray300;
      border: px(1) solid $common-gray-border;

      &:focus {
        background-color: $common-gray300;
      }
    }

    &.-gray-out {
      color: $common-black;
      border: px(1) solid $common-gray500;

      &:focus {
        opacity: $hover-opacity;
      }
    }

    &:focus,
    &.tap {
      color: $common-white;

      @each $key, $color in $pressed-colors {
        &.-#{$key} {
          background-color: $color;
        }
      }

      &.-background_none {
        color: $common-primary-pressed;
      }
    }
  }

  &--clicked {
    color: $common-gray700;
    // TODO: irregular
    border: px(1) solid #bdbdbd;

    &.-disabled {
      color: $common-gray300;
      border-color: $common-gray300;

      &:focus {
        color: $common-gray300;
        border-color: $common-gray300;
      }
    }

    &:focus,
    &.tap {
      color: $common-gray900;
      background-color: #faf5e2;
      border-color: $common-gray900;
    }
  }

  &--cancel {
    color: $common-gray700;
    // TODO: irregular
    border: px(1) solid #bdbdbd;

    &.-disabled {
      color: $common-gray300;
      border-color: $common-gray300;

      &:focus {
        color: $common-gray300;
        border-color: $common-gray300;
      }
    }

    &:focus,
    &.tap {
      color: $common-gray900;
      border-color: $common-gray900;
    }
  }

  &--aside {
    color: $common-white;
    background: $common-black;

    &.-disabled {
      &:focus {
        background-color: #000000;
      }
    }

    &.-gray-out {
      background: $common-gray300;

      &:focus {
        background-color: $common-gray500;
      }
    }

    &:focus,
    &.tap {
      background-color: $common-black-sub;
    }
  }

  &--text {
    color: $common-primary;
    width: auto;

    &.-disabled {
      color: $common-gray300;
      &:focus {
        color: $common-gray300;
      }
    }

    &:focus,
    &.tap {
      color: $common-primary-pressed;
    }
  }

  &--square {
    width: rem(40);
    padding: rem(9) rem(4) rem(10);
    color: $common-primary;
    border: px(1) solid $common-primary;

    &.-primary {
      color: $common-primary;
      border: px(1) solid $common-primary;
    }

    &.-blue {
      color: $customize-blue;
      border: px(1) solid $customize-blue;
    }

    &.-green {
      color: $customize-green;
      border: px(1) solid $customize-green;
    }

    &.-yellow {
      color: $customize-yellow;
      border: px(1) solid $customize-yellow;
    }

    &.-pink {
      color: $customize-pink;
      border: px(1) solid $customize-pink;
    }

    &.-red {
      color: $customize-red;
      border: px(1) solid $customize-red;
    }

    &.-orange {
      color: $customize-orange;
      border: px(1) solid $customize-orange;
    }

    &.-bisque {
      color: $customize-bisque;
      border: px(1) solid $customize-bisque;
    }

    &.-disabled {
      color: $common-black;
      border: px(1) solid $common-gray500;
    }

    &.-gray-out {
      color: $common-black;
      border: px(1) solid $common-gray500;
    }
  }

  &.-border_none {
    border: none;
  }

  &.-width_auto {
    width: auto;
  }

  &__icon {
    position: absolute;
    top: 0;
    left: rem(40);
    display: block;
  }
}
</style>
